import { useState, useRef } from "react";
import { toast } from "sonner";
import { FileUp, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * LicenseUploadCard — persistent card on the broker dashboard for uploading the
 * CMA licence PDF/image. Shown as `PENDING` until `cma_license_storage_path` is
 * populated on the agency, then flips to a `VERIFIED` pill.
 *
 * File is streamed multipart to POST /api/agency/upload-license. Max 10 MB per
 * backend contract; validated client-side too to save the round-trip.
 */

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = ["application/pdf", "image/png", "image/jpeg", "image/webp"];

export default function LicenseUploadCard({ agency, onUploaded }) {
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const uploaded = !!agency?.cma_license_storage_path;
    const isMoE = agency?.institution_type === "moe";
    const docLabel = isMoE
        ? "Ministry of Education Licence / Registration Document"
        : "CMA Licence Document";
    const docNoun  = isMoE ? "MoE registration document" : "CMA licence";

    const pick = () => inputRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        // Reset the input so re-picking the same file still triggers the handler.
        e.target.value = "";
        if (!file) return;
        if (!ALLOWED_MIME.includes(file.type)) {
            toast.error(`Unsupported file type: ${file.type || "unknown"}`);
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error(`Too large: ${(file.size / 1_000_000).toFixed(1)} MB — max 10 MB`);
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/agency/upload-license", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${docLabel} uploaded — thank you.`);
            onUploaded?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-border bg-card rounded p-4 flex items-start gap-4" data-testid="license-upload-card">
            <div className="mt-1">
                {uploaded
                    ? <CheckCircle2 className="w-6 h-6 text-success" />
                    : <FileUp className="w-6 h-6 text-primary" />
                }
            </div>
            <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="overline">{docLabel}</div>
                    <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold " + (
                        uploaded
                            ? "bg-success/10 text-success"
                            : "bg-primary/10 text-primary"
                    )}>
                        {uploaded ? "VERIFIED" : "PENDING"}
                    </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                    {uploaded
                        ? `We have your ${docNoun} on file. Re-upload only if the document was updated.`
                        : `Upload a PDF or image of your ${docNoun}. Required for compliance review.`}
                </p>
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={onFile}
                    data-testid="license-upload-input"
                />
                <button
                    type="button"
                    disabled={busy}
                    onClick={pick}
                    data-testid="license-upload-btn"
                    className={
                        "mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-semibold " +
                        (uploaded
                            ? "border border-border hover:bg-secondary/40"
                            : "bg-primary text-primary-foreground hover:opacity-90")
                    }
                >
                    <FileUp className="w-3.5 h-3.5" />
                    {busy ? "Uploading…" : uploaded ? "Re-upload" : "Upload licence"}
                </button>
            </div>
        </div>
    );
}
